/**
 * Proactive AWS credential refresh manager.
 *
 * AWS temporary credentials (obtained via CognitoIdentityCredentials)
 * expire after a configured duration. When they expire during idle,
 * API calls fail with auth errors like "NotFound" from Bindle or
 * 403 from API Gateway — because the backend rejects the stale signature.
 *
 * This manager proactively refreshes credentials before they expire so
 * API calls always have valid credentials available.
 *
 * It also refreshes on visibilitychange (user returns from background tab)
 * to catch credentials that expired while the tab was idle.
 */

import AWS from 'aws-sdk';
import { getAwsCredentials } from 'src/authentication/cognito';
import { logger } from 'src/utils/logger';

const REFRESH_BUFFER_MS = 2 * 60 * 1000;
const MIN_REFRESH_INTERVAL_MS = 60 * 1000;
const FALLBACK_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let isRunning = false;

function getMsUntilCredentialExpiry(): number | null {
	try {
		const credentials = AWS.config
			.credentials as AWS.CognitoIdentityCredentials | null;

		if (!credentials || !credentials.expireTime) {
			return null;
		}

		const expiryMs = new Date(credentials.expireTime).getTime();
		const remaining = expiryMs - Date.now();

		return remaining > 0 ? remaining : 0;
	} catch {
		return null;
	}
}

async function performRefresh(): Promise<void> {
	try {
		logger.info('[CredentialRefresh] Proactively refreshing AWS credentials');

		const credentials = AWS.config
			.credentials as AWS.CognitoIdentityCredentials | null;

		if (credentials && credentials.needsRefresh()) {
			credentials.expired = true;
			await getAwsCredentials();
			logger.info('[CredentialRefresh] Credentials refreshed successfully');
		} else if (!credentials) {
			await getAwsCredentials();
			logger.info('[CredentialRefresh] Credentials obtained');
		} else {
			logger.info('[CredentialRefresh] Credentials still valid, skipping');
		}
	} catch (err: unknown) {
		const error = err instanceof Error ? err : new Error(String(err));

		logger.error('[CredentialRefresh] Proactive refresh failed', error);
	}

	scheduleNextRefresh();
}

function scheduleNextRefresh(): void {
	clearScheduledRefresh();

	if (!isRunning) {
		return;
	}

	const msUntilExpiry = getMsUntilCredentialExpiry();
	let delay: number;

	if (msUntilExpiry === null) {
		delay = FALLBACK_REFRESH_INTERVAL_MS;
	} else {
		delay = Math.max(
			msUntilExpiry - REFRESH_BUFFER_MS,
			MIN_REFRESH_INTERVAL_MS
		);
	}

	logger.info(
		`[CredentialRefresh] Next refresh in ${Math.round(delay / 1000)}s`
	);

	refreshTimer = setTimeout(() => {
		performRefresh();
	}, delay);
}

function clearScheduledRefresh(): void {
	if (refreshTimer !== null) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
}

function handleVisibilityChange(): void {
	if (document.hidden || !isRunning) {
		return;
	}

	const msUntilExpiry = getMsUntilCredentialExpiry();

	if (msUntilExpiry === null || msUntilExpiry < REFRESH_BUFFER_MS) {
		logger.info(
			'[CredentialRefresh] Tab visible, credentials near-expiry — refreshing'
		);
		performRefresh();
	} else {
		scheduleNextRefresh();
	}
}

/**
 * Starts the proactive credential refresh manager.
 */
export function startCredentialRefreshManager(): void {
	if (isRunning) {
		return;
	}

	isRunning = true;
	document.addEventListener('visibilitychange', handleVisibilityChange);
	scheduleNextRefresh();

	logger.info('[CredentialRefresh] Manager started');
}

export function stopCredentialRefreshManager(): void {
	isRunning = false;
	clearScheduledRefresh();
	document.removeEventListener('visibilitychange', handleVisibilityChange);

	logger.info('[CredentialRefresh] Manager stopped');
}
